"use client";
// @ts-strict-ignore
import { useEffect, useRef, useState } from "react";
import orderBy from "lodash/orderBy";
import { OPTIMIZELY_FEATURE_FLAGS } from "analytics/optimizely/config";
import type { FeatureVariablesType } from "hooks/useFeatureFlags";
import { useFeatureFlags } from "hooks/useFeatureFlags";

import useSWR from "swr";

import { POLL_PRICES_API_INTERVAL } from "config";
import { DEFAULT_TIMEFRAME } from "constants/prices";
import type { CoinPriceOverview } from "types/prices";
import { PriceSortField, SortDirection } from "types/prices";
import type { ExchangePriceData } from "utils/prices";
import {
  fetchAllPrices,
  fetchAssetPrice,
  fetchMarketFilters,
  fetchTokenizedStocksPrices,
  fetchTokenizedStockDetail
} from "utils/prices";
import {
  addPriceDataToCoinDetail,
  getAllPriceOverviewsInPricesPage,
  getAllTokenizedStocks,
  getExchangeOrExternalPriceData,
  getPriceOverviewFromRawPriceData,
  getSortComparator
} from "utils/prices-utils";
import type { AxiosResponse } from "axios";
import axios from "axios";

import { getCoinGeckoId } from "utils/getCoinGeckoID";
import { formatPrice, lineData } from "utils/coinPrices";

// Define ExchangePricesResponse type here until it's properly exported from utils/prices
type ExchangePricesResponse = ExchangePriceData[];

type PriceFetcher = {
  coinEntries: CoinPriceOverview[];
  coinName?: string;
  coinOverview?: CoinPriceOverview;
  hideRange?: boolean;
  timeFrame?: string;
  customKeyName?: string;
};

const getStockAPIIntervals = (interval: string) => {
  switch (interval) {
    case "1d":
      return "DAY";
    case "1w":
      return "WEEK";
    case "1m":
      return "MONTH";
    case "1y":
      return "YEAR";
    default:
      return "DAY";
  }
};

export const isCoinGeckoChartDataEnabled = (
  coin: string,
  fiat: string,
  interval: string,
  featureFlags: Partial<Record<OPTIMIZELY_FEATURE_FLAGS, boolean>>,
  featureVariables: FeatureVariablesType
) => {
  if (!featureFlags.mobile_coingecko_for_missing_chart_data) {
    return false;
  }

  const supportedFiats =
    featureVariables[OPTIMIZELY_FEATURE_FLAGS.WEB_COINGECKO_ALL_TIME_DATA]
      ?.fiat_pairs?.currency ?? [];

  if (!supportedFiats.includes(fiat)) {
    return false;
  }

  const includedTimeFrames =
    featureVariables[OPTIMIZELY_FEATURE_FLAGS.COINGECKO_FOR_MISSING_CHART_DATA]
      ?.includedTimeFrames;
  if (!includedTimeFrames?.includes(interval)) {
    return false;
  }

  const includeAllCoins =
    featureVariables[OPTIMIZELY_FEATURE_FLAGS.COINGECKO_FOR_MISSING_CHART_DATA]
      ?.includeAllCoins;
  if (includeAllCoins) {
    return true;
  }

  const includedCoins =
    featureVariables[OPTIMIZELY_FEATURE_FLAGS.COINGECKO_FOR_MISSING_CHART_DATA]
      ?.includedCoins;
  return includedCoins?.includes(coin?.toLowerCase());
};

export const INTERVALS = {
  hour: "1h",
  day: "1d",
  week: "1w",
  oneMonth: "1m",
  oneYear: "1y",
  allTime: "all"
} as const;

export const getCoinGeckoStartTimestamp = (interval: string) => {
  if (interval === INTERVALS.allTime) return 0;

  const date = new Date();
  switch (interval) {
    case INTERVALS.hour:
      date.setHours(date.getHours() - 1); // 1 hour ago
      date.setSeconds(0, 0); // for HTTP caching, avoid changing `from` query param too frequently
      break;
    case INTERVALS.day:
      date.setDate(date.getDate() - 1); // 1 day ago
      date.setMinutes(0, 0, 0);
      break;
    case INTERVALS.week:
      date.setDate(date.getDate() - 7); // 1 week ago
      date.setHours(0, 0, 0, 0);
      break;
    case INTERVALS.oneMonth:
      date.setMonth(date.getMonth() - 1); // 1 month ago
      date.setHours(0, 0, 0, 0);
      break;
    case INTERVALS.oneYear:
      date.setFullYear(date.getFullYear() - 1); // 1 year ago
      date.setHours(0, 0, 0, 0);
      break;
    default:
      break;
  }
  return Math.floor(date.getTime() / 1000); // unix timestamp
};

const getCoinGeckoData = async (
  coin: string,
  latestExchangeTimestamp: number,
  precision: number,
  interval: string,
  featureVariables: FeatureVariablesType
) => {
  try {
    const coinGeckoId = getCoinGeckoId(
      coin,
      featureVariables[OPTIMIZELY_FEATURE_FLAGS.COINGECKO_TOKEN_MAP]
        .additionalTokenMap
    );

    if (!coinGeckoId) return [];

    const startTimestamp = getCoinGeckoStartTimestamp(interval);
    // coingecko uses unix ts in s, exchange uses ms - convert to unix
    const endTimestamp = Boolean(latestExchangeTimestamp)
      ? latestExchangeTimestamp / 1000
      : Math.floor(Date.now() / 1000);
    if (startTimestamp >= endTimestamp) return [];

    const response = await axios.get(
      `/api/coins/${coinGeckoId}/market_chart/range?vs_currency=usd&from=${startTimestamp}&to=${endTimestamp}&precision=${precision}`
    );
    return response?.data?.prices?.map((price: any) => {
      return { x: price[0], y: price[1] };
    });
  } catch (_e) {
    // Silently fail if we cannot get coingecko data and proceed with displaying 1 year of exchange data
    return [];
  }
};

export const useSingleAssetPriceFetcher = ({
  coinOverview
}: {
  coinOverview?: CoinPriceOverview;
  customKeyName?: string;
}) => {
  const [timeframe, setTimeframe] = useState(
    coinOverview?.exchangePrices ? DEFAULT_TIMEFRAME : "1y"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [displayTimeframe, setDisplayTimeframe] = useState(
    coinOverview?.exchangePrices ? DEFAULT_TIMEFRAME : "1y"
  );
  const [cachedPriceData, setCachedPriceData] = useState<any>(null);

  const { featureFlags, featureVariables } = useFeatureFlags();

  const tokenizedStockSymbols = useRef<string[]>([]);

  const fetcher = async (_: any, timeframe: any) => {
    setIsLoading(true);
    let coinGeckoDataRanges = [];

    const coinGeckoChartDataEnabled = isCoinGeckoChartDataEnabled(
      coinOverview?.coin,
      "USD", // use USD as default fiat for now
      timeframe,
      featureFlags,
      featureVariables
    );

    // avoid extra network calls that throw 404's for tokens not on gemini
    // prepend all time data to static data fetched from server for unsupported tokens
    if (!coinOverview.exchangePrices) {
      // Retrieve the known list of tokenized stock symbols if we don't already know the list
      if (tokenizedStockSymbols.current.length === 0) {
        tokenizedStockSymbols.current = await getAllTokenizedStocks().then(
          (ts) => ts.map((t) => t.coin)
        );
      }

      if (tokenizedStockSymbols.current.includes(coinOverview.coin)) {
        const interval = getStockAPIIntervals(timeframe);

        try {
          // Try to get detailed data with timeframe
          const stockDetail = await fetchTokenizedStockDetail(
            coinOverview.coin,
            interval
          );
          const result = {
            ...stockDetail.data,
            isTokenizedStock: true,
            priceRange: lineData(stockDetail.data.range)
          };
          return result;
        } catch (error) {
          // Silence error but still fallback
          // Fall back to the market overview if the detailed endpoint fails
          const mostRecentCoinOverview =
            await fetchTokenizedStocksPrices().then((res) =>
              res.data.find((epr) => epr.symbol === coinOverview.symbol)
            );
          const result = {
            ...mostRecentCoinOverview,
            isTokenizedStock: true,
            priceRange: lineData(mostRecentCoinOverview.range)
          };
          return result;
        }
      }

      if (timeframe === "all" || coinGeckoChartDataEnabled) {
        const latestExchangeTimestamp = coinOverview?.priceRange?.[0]?.x;

        // temp solution to keep the precision in sync with data from api
        const precision = coinOverview?.priceRange?.[0]?.y
          ? formatPrice(coinOverview?.priceRange?.[0]?.y)?.split(".")?.[1]
              ?.length
          : 8;
        coinGeckoDataRanges = await getCoinGeckoData(
          coinOverview?.coin,
          latestExchangeTimestamp,
          precision,
          timeframe,
          featureVariables
        );
      }

      const newRanges = [...coinGeckoDataRanges, ...coinOverview?.priceRange];
      const result = {
        ...coinOverview,
        isTokenizedStock: false,
        priceRange: lineData(newRanges.filter((_) => _?.y !== 0))
      };
      return result;
    } else {
      // when fetching all, try to grab 1 year of exchange data and append to the coingecko data
      const newTimeFrame = timeframe === "all" ? "1y" : timeframe;
      const assetPrice = await fetchAssetPrice(
        coinOverview?.coin,
        newTimeFrame
      );

      let assetRange = assetPrice?.data?.range;

      if (timeframe === "1h") {
        // Currently, the API does not support fetching data specifically for a 1-hour timeframe.
        // As a workaround, we fetch more data than needed and then slice the last 60 data points to represent the last hour.
        // This is a temporary solution until the API is updated to handle 1-hour timeframes directly.
        assetRange = assetRange.slice(-60);
      }

      if (timeframe === "all" || coinGeckoChartDataEnabled) {
        const latestExchangeTimestamp = assetRange?.[0]?.x;
        const precision = assetPrice?.data?.priceInputDecimals ?? 2;

        coinGeckoDataRanges = await getCoinGeckoData(
          coinOverview?.coin,
          latestExchangeTimestamp,
          precision,
          timeframe,
          featureVariables
        );
      }

      // merge ranges to provide all time data in the chart
      const priceRanges =
        Boolean(coinGeckoDataRanges) && coinGeckoDataRanges.length > 0
          ? [...coinGeckoDataRanges, ...(assetRange ?? [])]
          : assetRange;

      const result = {
        ...coinOverview,
        symbol: assetPrice?.data?.symbol,
        isTokenizedStock: false,
        priceOverview: getPriceOverviewFromRawPriceData(assetPrice.data),
        priceRange: lineData(priceRanges.filter((_) => _?.y !== 0))
      };
      return result;
    }
  };
  const { data: priceData } = useSWR(
    [coinOverview?.name, timeframe],
    ([key, timeframe]) => fetcher(key, timeframe),
    {
      refreshInterval: POLL_PRICES_API_INTERVAL,
      onSuccess: (data) => {
        setCachedPriceData(data);
        setDisplayTimeframe(timeframe);
        setIsLoading(false);
      },
      onError: () => {
        setIsLoading(false);
      }
    }
  );

  // Custom timeframe setter that updates the requested timeframe but keeps displaying the current data
  const handleSetTimeframe = (newTimeframe: string) => {
    // Only update if it's different from the current timeframe
    if (newTimeframe !== timeframe) {
      setIsLoading(true);
      setTimeframe(newTimeframe);
    }
  };

  return {
    priceData: cachedPriceData || (priceData as any),
    timeframe: displayTimeframe, // This is what the UI will display
    requestedTimeframe: timeframe, // This is what's being fetched
    setTimeframe: handleSetTimeframe,
    precision: coinOverview?.priceInputDecimals ?? 2,
    isLoading
  };
};

/**
 * Fetch all necessary data sources for prices page
 */
const fetchDataSources = async (timeFrame: string) => {
  try {
    // Fetch data sources in parallel using Promise.allSettled for better error handling
    const results = await Promise.allSettled([
      getAllPriceOverviewsInPricesPage(),
      fetchAllPrices(timeFrame),
      fetchMarketFilters(),
      fetchTokenizedStocksPrices(),
      getAllTokenizedStocks()
    ]);

    // Safely extract values or use fallbacks
    const coinPriceOverviews =
      results[0].status === "fulfilled" ? results[0].value : [];
    const allPrices = results[1].status === "fulfilled" ? results[1].value : [];
    const marketFilterData =
      results[2].status === "fulfilled" ? results[2].value : { filters: [] };
    const tokenizedStocksPrices =
      results[3].status === "fulfilled" ? results[3].value : { data: [] };
    const tokenizedStocksContentful =
      results[4].status === "fulfilled" ? results[4].value : [];

    return {
      coinPriceOverviews,
      allPrices,
      marketFilterData,
      tokenizedStocksPrices,
      tokenizedStocksContentful
    };
  } catch (_error) {
    // Return empty data to avoid breaking the entire function
    return {
      coinPriceOverviews: [],
      allPrices: [],
      marketFilterData: { filters: [] },
      tokenizedStocksPrices: { data: [] },
      tokenizedStocksContentful: []
    };
  }
};

/**
 * Organize coins by their filter categories
 */
const organizeByFilterCategory = (
  coinPriceOverviews: CoinPriceOverview[],
  filters: any[] = []
) => {
  const coinsList: CoinPriceOverview[] = [];

  filters.forEach((filter) => {
    coinPriceOverviews.forEach((coin) => {
      // If coin is explicitly listed in this filter's assets, add it with this filter type
      if ((filter?.assets || []).includes(coin.coin)) {
        coinsList.push({ ...coin, type: filter?.type });
      }
      // If coin is not yet in any list, add it with the default filter type
      else if (
        !(
          coinsList.some((data) => data?.coin === coin.coin) &&
          coinsList.some((data) => data?.type === filters[0]?.type)
        ) &&
        filters[0]
      ) {
        coinsList.push({ ...coin, type: filters[0].type });
      }
    });
  });

  return coinsList;
};

/**
 * Process regular cryptocurrency entries with price data
 */
const processRegularCoins = (
  coinsList: CoinPriceOverview[],
  allPrices: any[]
): CoinPriceOverview[] => {
  if (!Array.isArray(allPrices)) {
    return coinsList; // Return original list to avoid breaking everything
  }

  return coinsList.map((coinData) => {
    try {
      const exchangeCoinData = allPrices.find(
        ({ coin }) => coin === coinData.coin
      );

      const rawPriceData = getExchangeOrExternalPriceData(
        coinData,
        exchangeCoinData || {}
      );
      return addPriceDataToCoinDetail(coinData, rawPriceData, true);
    } catch (_error) {
      // Return the original coin data to prevent the entire map from failing
      return coinData;
    }
  });
};

/**
 * Create default price data for assets when API data is not available
 */
const createDefaultPriceData = (symbol: string): ExchangePriceData =>
  ({
    symbol: `${symbol}USD`,
    range: [],
    ask: "0",
    bid: "0",
    "24hrDelta": "0",
    "24hrPercentDelta": "0",
    rangeDelta: "0",
    rangePercentDelta: "0",
    marketCap: { currency: "USD", value: "0" },
    supply: { currency: "USD", value: "0" },
    volume: { currency: "USD", value: "0" },
    allTimeHigh: { currency: "USD", value: "0" },
    from: symbol,
    to: "USD",
    fromDisplayName: symbol,
    toDisplayName: "US Dollar",
    "24hrMin": "0",
    "24hrMax": "0",
    instantMarketOpen: false,
    limitOrderOpen: false,
    stopLimitOrderOpen: false,
    marketOrderOpen: false,
    priceInputDecimals: 2,
    isSyntheticPair: false,
    isPerpPair: false,
    lastTradePrice: "0"
  }) as ExchangePriceData;

/**
 * Process a single tokenized stock with price data
 */
const processTokenizedStock = (
  stockContentful: CoinPriceOverview,
  tokenizedStocksPrices:
    | AxiosResponse<ExchangePricesResponse>
    | { data: any[] },
  defaultFilterType: string = "AllMarketFilter" // Add default filter type parameter
): CoinPriceOverview | null => {
  try {
    // Try to find matching price data from API
    let stockData = null;
    if (
      tokenizedStocksPrices?.data &&
      Array.isArray(tokenizedStocksPrices.data)
    ) {
      stockData = tokenizedStocksPrices.data.find(
        (apiStock) =>
          apiStock.symbol.replace("USD", "") === stockContentful.coin
      );
    }

    // Create a stock entry with exchangePrices set to true to use exchange data format
    // and add the default filter type to ensure it shows up correctly
    const updatedStockContentful = {
      ...stockContentful,
      exchangePrices: true, // Override to ensure correct price data format is used
      type: defaultFilterType, // Add type property for filtering in the UI
      isTokenizedStock: true // Add flag to identify tokenized stocks
    };

    if (stockData) {
      // Use API data if available
      return addPriceDataToCoinDetail(updatedStockContentful, stockData, true);
    } else {
      // Use default data if no API data is available
      return addPriceDataToCoinDetail(
        updatedStockContentful,
        createDefaultPriceData(stockContentful.coin),
        true
      );
    }
  } catch (_error) {
    return null;
  }
};

/**
 * Process all tokenized stocks data
 */
const processTokenizedStocks = (
  tokenizedStocksContentful: CoinPriceOverview[],
  tokenizedStocksPrices:
    | AxiosResponse<ExchangePricesResponse>
    | { data: any[] },
  defaultFilterType: string = "AllMarketFilter" // Add default filter type parameter
): CoinPriceOverview[] => {
  if (!tokenizedStocksContentful || tokenizedStocksContentful.length === 0) {
    return [];
  }

  // Use type assertion to handle our fallback data structure
  const stocksPrices =
    tokenizedStocksPrices as AxiosResponse<ExchangePricesResponse>;

  const results = tokenizedStocksContentful
    .map((stockContentful) =>
      processTokenizedStock(stockContentful, stocksPrices, defaultFilterType)
    )
    .filter(Boolean); // Remove any null entries

  return results;
};

/**
 * Hook for fetching and sorting price data for both cryptocurrencies and tokenized stocks
 */
const useSortedPriceFetcher = ({
  coinEntries,
  timeFrame = DEFAULT_TIMEFRAME,
  customKeyName = "allPrices"
}: PriceFetcher) => {
  const [sortedPriceRows, setRows] = useState(coinEntries);
  const [filters, setFilters] = useState([]);
  // allow pre-rendering single coin
  const [sortField, setSortField] = useState(PriceSortField.MarketCap);
  const [sortDirection, setSortDirection] = useState(SortDirection.Descending);
  // We use a secondary sort field to make sure that the empty coins stay at the bottom of the table
  const [secondarySortField, setSecondarySortField] = useState(
    PriceSortField.Price
  );

  if (
    sortField === PriceSortField.Price &&
    secondarySortField !== PriceSortField.MarketCap
  ) {
    setSecondarySortField(PriceSortField.MarketCap);
  }
  if (
    sortField === PriceSortField.MarketCap &&
    secondarySortField !== PriceSortField.Price
  ) {
    setSecondarySortField(PriceSortField.Price);
  }

  const fetcher = async (_key: string) => {
    if (coinEntries.length === 0) throw new Error("No Contentful Entries");

    // 1. FETCH ALL DATA SOURCES
    const {
      coinPriceOverviews,
      allPrices,
      marketFilterData,
      tokenizedStocksPrices,
      tokenizedStocksContentful
    } = await fetchDataSources(timeFrame);

    const { filters = [] } = marketFilterData;

    // Create a custom filter for tokenized stocks if they exist
    let tokenizedStockSymbols: string[] = []; // Define outside the if block for wider scope

    if (tokenizedStocksContentful && tokenizedStocksContentful.length > 0) {
      // Extract all tokenized stock symbols
      tokenizedStockSymbols = tokenizedStocksContentful.map(
        (stock) => stock.coin
      );

      // Add a custom filter for tokenized stocks
      const tokenizedStocksFilter = {
        name: "Tokenized Stocks",
        description: "Tokenized stock assets on Gemini",
        assets: tokenizedStockSymbols,
        type: "TokenizedStocksMarketFilter" // Use a dedicated filter type
      };

      // Add the tokenized stocks filter to the filters array
      filters.push(tokenizedStocksFilter);
    }

    // 2. ORGANIZE COINS BY FILTER CATEGORY
    const coinsList = organizeByFilterCategory(coinPriceOverviews, filters);

    // 3. INITIALIZE COMBINED DATA ARRAY
    let allCoinsData: CoinPriceOverview[] = [];

    // 4. PROCESS REGULAR COINS
    try {
      if (coinsList.length > 0) {
        const regularCoinsData = processRegularCoins(coinsList, allPrices);
        allCoinsData = regularCoinsData;
      }
    } catch (_error) {
      // Continue with existing data if processing regular coins fails
    }

    // 5. PROCESS TOKENIZED STOCKS
    try {
      if (tokenizedStocksContentful?.length > 0) {
        // Ensure tokenizedStocksPrices has the correct structure
        const stocksPricesData =
          tokenizedStocksPrices && tokenizedStocksPrices.data
            ? tokenizedStocksPrices
            : { data: [] };

        // Use dedicated TokenizedStocksMarketFilter for tokenized stocks
        const defaultFilterType = "TokenizedStocksMarketFilter";

        const tokenizedStocksData = processTokenizedStocks(
          tokenizedStocksContentful,
          stocksPricesData,
          defaultFilterType
        );

        if (tokenizedStocksData.length > 0) {
          allCoinsData = [...allCoinsData, ...tokenizedStocksData];
        }
      }
    } catch (_error) {
      // Continue with existing data if processing tokenized stocks fails
    }

    // 6. RETURN COMBINED DATA
    return { allCoinsData, filters };
  };

  const { data: priceData } = useSWR(customKeyName, fetcher, {
    refreshInterval: POLL_PRICES_API_INTERVAL
  });

  useEffect(() => {
    if (!!priceData?.allCoinsData) {
      const rows = orderBy(
        priceData?.allCoinsData,
        [
          (currency) =>
            getSortComparator(
              sortField,
              sortDirection,
              currency as CoinPriceOverview
            ),
          (currency) =>
            getSortComparator(
              secondarySortField,
              sortDirection,
              currency as CoinPriceOverview
            )
        ],
        [sortDirection, sortDirection]
      );
      setRows(rows);
      setFilters(priceData?.filters);
    }
  }, [sortField, sortDirection, priceData, secondarySortField]);

  return {
    sortedPriceRows,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    filters
  };
};

export default useSortedPriceFetcher;
